import { useMemo, useState } from "react";
import { api } from "../../lib/api";

// Sharpness thresholds tuned for the 1600 px web variant — see
// internal/imagepipeline/scoring/scorer.go for the underlying metric.
const SHARP_BLURRY_THRESHOLD = 80;
const EXPOSURE_EXTREME_THRESHOLD = 0.45;

type CullData = NonNullable<Awaited<ReturnType<typeof api.getGalleryCull>>>;

interface CullTabProps {
  gallerySlug: string;
  data: CullData | null;
  loading: boolean;
  error: string | null;
  onAssetDeleted: (assetId: string) => Promise<void>;
}

export function CullTab({ gallerySlug, data, loading, error, onAssetDeleted }: CullTabProps) {
  const [sortBy, setSortBy] = useState<"order" | "sharpness" | "exposure">("sharpness");
  const [busy, setBusy] = useState<string | null>(null);

  // Sort + cluster derivation is the expensive bit. Memoize so a re-render
  // triggered by the delete button doesn't re-sort the whole array.
  const { clusters, singletons } = useMemo(() => {
    if (!data || data.count === 0) {
      return { sorted: [] as CullData["assets"], clusters: [] as Array<{ id: string; assets: CullData["assets"] }>, singletons: [] as CullData["assets"] };
    }
    const sortedLocal = [...data.assets].sort((a, b) => {
      if (sortBy === "sharpness") return (a.sharpness ?? 1e9) - (b.sharpness ?? 1e9);
      if (sortBy === "exposure") return Math.abs(b.exposure ?? 0) - Math.abs(a.exposure ?? 0);
      return 0;
    });
    const clustersLocal: Array<{ id: string; assets: CullData["assets"] }> = [];
    const singletonsLocal: CullData["assets"] = [];
    const seen = new Set<string>();
    for (const a of sortedLocal) {
      if (a.cluster_id && !seen.has(a.cluster_id)) {
        seen.add(a.cluster_id);
        clustersLocal.push({ id: a.cluster_id, assets: sortedLocal.filter((x) => x.cluster_id === a.cluster_id) });
      } else if (!a.cluster_id) {
        singletonsLocal.push(a);
      }
    }
    return { sorted: sortedLocal, clusters: clustersLocal, singletons: singletonsLocal };
  }, [data, sortBy]);

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading cull data…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!data || data.count === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
        <p style={{ margin: 0, color: "var(--muted)" }}>Nothing to cull yet — upload some photos first.</p>
      </div>
    );
  }

  async function handleDelete(assetId: string) {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    setBusy(assetId);
    try { await onAssetDeleted(assetId); } finally { setBusy(null); }
  }

  function badge(label: string, cssVar: string) {
    return (
      <span style={{
        background: `color-mix(in srgb, var(${cssVar}) 18%, transparent)`,
        color: `var(${cssVar})`,
        border: `1px solid color-mix(in srgb, var(${cssVar}) 40%, transparent)`,
        fontSize: ".7rem", padding: ".05rem .35rem", borderRadius: 999,
        fontWeight: 600,
      }}>{label}</span>
    );
  }

  function renderTile(a: CullData["assets"][number]) {
    const blurry = a.sharpness != null && a.sharpness < SHARP_BLURRY_THRESHOLD;
    const overexp = a.exposure != null && a.exposure > EXPOSURE_EXTREME_THRESHOLD;
    const underexp = a.exposure != null && a.exposure < -EXPOSURE_EXTREME_THRESHOLD;
    return (
      <div
        key={a.id}
        style={{
          position: "relative",
          borderRadius: 8, overflow: "hidden",
          background: "var(--card)",
          border: blurry ? "2px solid var(--warning)" : "1px solid var(--border)",
        }}
      >
        <div style={{ aspectRatio: "1", overflow: "hidden", background: "var(--border)" }}>
          {a.thumb_url && (
            <img src={a.thumb_url} alt={a.filename} loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          )}
        </div>
        <div style={{ padding: ".5rem .65rem", fontSize: ".8rem", display: "flex", flexDirection: "column", gap: ".25rem" }}>
          <div style={{ display: "flex", gap: ".25rem", flexWrap: "wrap" }}>
            {!a.scored && badge("scoring…", "--slate")}
            {blurry && badge("blurry", "--warning")}
            {overexp && badge("over", "--danger")}
            {underexp && badge("under", "--info")}
          </div>
          <div style={{ color: "var(--muted)", fontSize: ".7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {a.scored ? `sharp ${(a.sharpness ?? 0).toFixed(0)} · exp ${(a.exposure ?? 0).toFixed(2)}` : a.filename}
          </div>
          <button
            className="btn-ghost"
            disabled={busy === a.id}
            onClick={() => void handleDelete(a.id)}
            style={{ fontSize: ".75rem", padding: ".2rem .5rem", color: "var(--danger)", alignSelf: "flex-start" }}
          >{busy === a.id ? "…" : "Delete"}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: "1rem", padding: ".75rem 1rem", display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <strong style={{ fontSize: ".9rem" }}>
          {data.count} photo{data.count === 1 ? "" : "s"}
        </strong>
        <span style={{ color: "var(--muted)", fontSize: ".85rem" }}>
          {data.duplicate_groups > 0
            ? `${data.duplicate_groups} near-duplicate group${data.duplicate_groups === 1 ? "" : "s"}`
            : "no near-duplicates"}
          {data.unscored > 0 && ` · ${data.unscored} still scoring`}
        </span>
        <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: ".85rem" }}>Sort:</span>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} style={{ fontSize: ".85rem" }}>
          <option value="sharpness">Blurriest first</option>
          <option value="exposure">Most extreme exposure</option>
          <option value="order">Upload order</option>
        </select>
        <small style={{ width: "100%", color: "var(--muted)", fontSize: ".75rem", margin: 0 }}>
          Cull view groups visually-similar shots and flags blurry / over- and under-exposed frames so you can drop the
          weakest before delivering. Older uploads need scoring — they auto-process as they're re-touched, or just upload them
          again for now.
        </small>
      </div>

      {clusters.length > 0 && (
        <section style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ margin: "0 0 .5rem", fontSize: ".95rem" }}>Near-duplicates</h3>
          <p style={{ color: "var(--muted)", fontSize: ".85rem", margin: "0 0 .75rem" }}>
            We grouped these by visual similarity. Pick the keeper in each row and drop the rest.
          </p>
          {clusters.map((g) => (
            <div key={g.id} style={{ marginBottom: "1rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: ".75rem" }}>
                {g.assets.map(renderTile)}
              </div>
            </div>
          ))}
        </section>
      )}

      {singletons.length > 0 && (
        <section>
          {clusters.length > 0 && <h3 style={{ margin: "0 0 .5rem", fontSize: ".95rem" }}>Everything else</h3>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: ".75rem" }}>
            {singletons.map(renderTile)}
          </div>
        </section>
      )}
      <small style={{ display: "block", marginTop: "1rem", color: "var(--muted)", fontSize: ".7rem" }}>
        Gallery slug: {gallerySlug}
      </small>
    </div>
  );
}
